import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from '../../hooks/useForm';
import QuestionRenderer from '../../components/questions/QuestionRenderer';
import QuestionToolbar from '../../components/questions/QuestionToolbar';
import { getValidationSummary } from '../../utils/validation';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import AiSidebar from '../../components/ai-sidebar/AiSidebar';
import './FormBuilder.css';
import AutoResizeTextarea from '../../components/forms/AutoResizeTextarea';

/* ─────────────────────────────────────────────
   Main FormBuilder page
───────────────────────────────────────────── */
const FormBuilder = () => {
  const { formId } = useParams();
  useDocumentTitle(formId ? 'Edit Form' : 'Build Form');
  const navigate = useNavigate();

  const {
    form,
    loading,
    saving,
    error,
    loadError,
    lastSaved,
    validationResult,
    updateFormData,
    updateSettings,
    addQuestion,
    updateQuestion,
    removeQuestion,
    moveQuestion,
    saveForm,
    publishForm,
  } = useForm(formId);

  const [localError, setLocalError] = useState(null);
  const [showAi, setShowAi] = useState(false);

  const handleTitleChange = (e) => updateFormData({ title: e.target.value });
  const handleDescriptionChange = (e) => updateFormData({ description: e.target.value });
  const handleCollectEmailChange = (e) => updateSettings({ collectEmail: e.target.checked });
  const handleAllowMultipleResponsesChange = (e) => updateSettings({ allowMultipleResponses: e.target.checked });

  const handleSaveDraft = async () => {
    try { await saveForm(); } catch (_) {}
  };

  const handlePublish = async () => {
    const hasRequiredQuestion = form.questions.some(q => q.required);
    if (!hasRequiredQuestion) {
      setLocalError('Cannot publish: Form must have at least one required question.');
      window.scrollTo(0, 0);
      return;
    }
    try {
      setLocalError(null);
      await publishForm();
      // Clear AI prompt history for this form now that it's published
      if (formId) {
        try { localStorage.removeItem(`formx_ai_history_${formId}`); } catch {}
      }
      navigate('/dashboard', { replace: true });
    } catch (_) {}
  };

  /**
   * Called by the AI sidebar when Gemini returns an updated form.
   * We patch in the new title, description, settings, and questions
   * via the existing updateFormData hook so the builder re-renders
   * and the auto-save fires.
   */
  const handleAiFormUpdate = (updatedFormData) => {
    updateFormData({
      title: updatedFormData.title ?? form.title,
      description: updatedFormData.description ?? form.description,
      settings: updatedFormData.settings ?? form.settings,
      questions: updatedFormData.questions ?? form.questions,
    });
  };

  /* ── Loading / error screens ── */
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading form...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="text-center">
          <h2 className="text-danger mb-3">Error Loading Form</h2>
          <p className="text-muted mb-4">{loadError}</p>
          <button onClick={() => window.history.back()} className="btn btn-primary">Go Back</button>
        </div>
      </div>
    );
  }

  /* ── Published view-only ── */
  const isPublished = form.status === 'PUBLISHED';
  if (isPublished) {
    return (
      <div className="container my-4">
        <div className="text-center mb-5">
          <h1 className="mb-3">{form.title}</h1>
          {form.description && <p className="text-muted fs-5 mb-3">{form.description}</p>}
          <div className="mb-4"><span className="badge bg-success fs-6">Published</span></div>
          <button onClick={() => window.history.back()} className="btn btn-outline-primary">← Back to Dashboard</button>
        </div>
        <div>
          {form.questions.map((question, index) => (
            <div key={question.id || index} className="card mb-4 border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-start mb-2">
                  <span className="fw-bold me-2">{index + 1}.</span>
                  <div className="flex-grow-1">
                    <h5 className="card-title mb-1">{question.title}</h5>
                    {question.required && <span className="text-danger">*</span>}
                  </div>
                </div>
                {question.description && <p className="text-muted mb-3 fst-italic">{question.description}</p>}
                <div className="mt-3">
                  <div className="p-3 bg-light border rounded">[Preview for {question.type}]</div>
                </div>
              </div>
            </div>
          ))}
          {form.questions.length === 0 && (
            <div className="text-center py-5"><p className="text-muted">This form has no questions yet.</p></div>
          )}
        </div>
      </div>
    );
  }

  /* ── Draft edit view ── */

  return (
    <>
      <div className="min-vh-100 pb-5" style={{ background: '#f0f4f8' }}>
        {/* ── Top Navigation Bar ── */}
        <nav
          className="glass-panel border-top-0 border-start-0 border-end-0 border-bottom sticky-top px-3 px-md-4 py-3 mb-4"
          style={{ borderRadius: 0, zIndex: 100 }}
        >
          <div
            className="container-fluid d-flex flex-wrap flex-md-nowrap justify-content-between align-items-center gap-3"
            style={{ maxWidth: '1000px' }}
          >
            {/* Left: Back + Title */}
            <div className="d-flex align-items-center gap-2 gap-md-3 flex-grow-1 overflow-hidden">
              <button onClick={() => navigate('/dashboard')} className="btn btn-ghost p-1 text-secondary flex-shrink-0">
                <i className="fa fa-chevron-left" aria-hidden="true" style={{ fontSize: '1.2rem' }} />
              </button>
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-1 gap-md-3 overflow-hidden">
                <h1 className="h5 mb-0 fw-semibold text-truncate" style={{ maxWidth: '100%' }}>
                  {form.title || 'Untitled Form'}
                </h1>
                <span
                  className={`badge rounded-pill align-self-start align-self-md-center ${
                    form.status === 'PUBLISHED' ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning-emphasis'
                  }`}
                  style={{ fontSize: '0.7rem' }}
                >
                  {form.status === 'PUBLISHED' ? 'Published' : 'Draft'}
                </span>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto ms-md-0">
              {/* AI Button */}
              <button
                id="ai-assistant-btn"
                className="ai-trigger-btn"
                onClick={() => setShowAi(true)}
                title="Open AI Form Assistant"
              >
                <span className="ai-pulse" />
                AI Assistant
              </button>

              {lastSaved && (
                <span className="text-muted small me-1 d-none d-lg-block">
                  Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}

              <button
                onClick={handleSaveDraft}
                disabled={saving || !validationResult.isValid}
                className="btn btn-light text-primary fw-medium border shadow-sm btn-sm px-3"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>

              <button
                onClick={handlePublish}
                disabled={saving || !validationResult.isValid || !form.id}
                className="btn btn-primary shadow-sm px-3 px-md-4 btn-sm"
              >
                Publish
              </button>

              {form.status === 'PUBLISHED' && (
                <a
                  href={`/form/${form.id}/fill`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-light border shadow-sm d-flex align-items-center justify-content-center btn-sm"
                  title="Preview"
                  style={{ width: '32px', height: '32px' }}
                >
                  <i className="fa fa-external-link" aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
        </nav>

        {/* ── Main Content ── */}
        <div className="container" style={{ maxWidth: '800px' }}>
          {/* Validation / Error */}
          {(!validationResult.isValid || error || localError) && (
            <div className="alert alert-danger shadow-sm border-0 mb-4">
              {localError || error || getValidationSummary(validationResult)}
            </div>
          )}

          {/* Form Header Card */}
          <div className="bg-white rounded-3 shadow-sm border-top border-5 border-primary mb-4 overflow-hidden">
            <div className="p-4">
              <AutoResizeTextarea
                placeholder="Form Title"
                value={form.title}
                onChange={handleTitleChange}
                className="form-control form-control-lg border-0 px-0 fw-bold mb-2 shadow-none form-title-textarea"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              />
              <AutoResizeTextarea
                placeholder="Form Description"
                value={form.description || ''}
                onChange={handleDescriptionChange}
                className="form-control border-0 px-0 text-secondary shadow-none"
                rows={1}
              />
            </div>

            <div className="bg-light px-4 py-3 border-top d-flex gap-4">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="collectEmail"
                  checked={form.settings.collectEmail}
                  onChange={handleCollectEmailChange}
                />
                <label className="form-check-label text-secondary small" htmlFor="collectEmail">
                  Collect Emails
                </label>
              </div>
              {form.settings.collectEmail && (
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allowMultiple"
                    checked={form.settings.allowMultipleResponses}
                    onChange={handleAllowMultipleResponsesChange}
                  />
                  <label className="form-check-label text-secondary small" htmlFor="allowMultiple">
                    Multiple Responses
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Questions List */}
          <div className="d-flex flex-column gap-3">
            {form.questions.map((question, index) => (
              <QuestionRenderer
                key={question.id || `temp-${index}`}
                question={question}
                questionIndex={index}
                onUpdate={updateQuestion}
                onRemove={removeQuestion}
                onMoveUp={(idx) => moveQuestion(idx, -1)}
                onMoveDown={(idx) => moveQuestion(idx, 1)}
                isFirst={index === 0}
                isLast={index === form.questions.length - 1}
              />
            ))}

            {form.questions.length === 0 && (
              <div
                className="text-center py-5 rounded-3 border-2 border-dashed border-secondary-subtle"
                style={{ border: '2px dashed #cbd5e1' }}
              >
                <div className="mb-3" style={{ fontSize: '2rem', opacity: 0.3 }}>📋</div>
                <p className="text-muted mb-2 fw-medium">No questions yet</p>
                <p className="text-muted small mb-0">
                  Use the toolbar below to add questions manually, or click{' '}
                  <button
                    className="btn btn-link p-0 small fw-semibold"
                    style={{ color: '#6366f1', textDecoration: 'none' }}
                    onClick={() => setShowAi(true)}
                  >
                    ✦ AI Assistant
                  </button>{' '}
                  to generate them instantly.
                </p>
              </div>
            )}
          </div>

          {/* Spacer for fixed toolbar */}
          <div style={{ height: '90px' }} />
        </div>

        {/* ── Floating Question Toolbar ── */}
        <div className="position-fixed bottom-0 start-0 w-100 mb-4 px-3 pb-2" style={{ zIndex: 90, pointerEvents: 'none' }}>
          <div className="d-flex justify-content-center justify-content-md-end w-100">
            <div style={{ pointerEvents: 'auto' }} className="me-md-4 me-lg-5">
              <QuestionToolbar onAddQuestion={addQuestion} />
            </div>
          </div>
        </div>
      </div>

      {/* ── AI Sidebar ── */}
      {showAi && (
        <AiSidebar
          formId={formId}
          form={form}
          onFormUpdate={handleAiFormUpdate}
          onClose={() => setShowAi(false)}
        />
      )}
    </>
  );
};

export default FormBuilder;
